import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/axios";
import "./EquipamentosPage.css";

import CriarEquipamento from "./popups/CriarEquipamento.jsx";
import EditarEquipamento from "./popups/EditarEquipamento.jsx";
import ExcluirEquipamento from "./popups/ExcluirEquipamento.jsx";

const TXT = (v) => (v == null ? "" : String(v));
const UP = (v) => TXT(v).toUpperCase();
const PAGE_SIZE = 500;
const NUM = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : null;
};
const KMNUM = (v) => {
  const raw = String(v ?? "").trim();
  if (!raw) return null;
  const asPoint = raw.replace(",", ".").replace("+", ".");
  const n = Number(asPoint);
  return Number.isFinite(n) ? n : null;
};

function IconEdit(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export default function EquipamentosPage({ onBack }) {
  const [lista, setLista] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [noFiltro, setNoFiltro] = useState(null);

  const [resumo, setResumo] = useState({
    total: 0,
    nos: 0,
    gerencias: 0,
    sbs: 0,
    chips: [],
  });

  const [abrirCriar, setAbrirCriar] = useState(false);
  const [editarItem, setEditarItem] = useState(null);
  const [excluirItem, setExcluirItem] = useState(null);

  const scrollRef = useRef(null);
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    document.body.classList.add("eqpm-skin");
    return () => document.body.classList.remove("eqpm-skin");
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 250);
    return () => clearTimeout(t);
  }, [qInput]);

  const carregarResumo = useCallback(async () => {
    try {
      let data;
      try {
        ({ data } = await api.get("/equipamentos/resumo"));
      } catch {
        ({ data } = await api.get("/bases/equipamentos/resumo"));
      }
      const raw = data?.data || {};
      const chips = Array.isArray(raw?.por_no)
        ? raw.por_no
            .map((r) => [UP(r?.no) || "SEM NÓ", Number(r?.qtd || 0)])
            .sort((a, b) => String(a[0]).localeCompare(String(b[0]), "pt-BR", { sensitivity: "base", numeric: true }))
        : [];

      setResumo({
        total: Number(raw?.total || 0),
        nos: Number(raw?.nos || 0),
        gerencias: Number(raw?.gerencias || 0),
        sbs: Number(raw?.sbs || 0),
        chips,
      });
    } catch {
      setResumo({ total: 0, nos: 0, gerencias: 0, sbs: 0, chips: [] });
    }
  }, []);

  const carregarPagina = useCallback(
    async ({ reset = false } = {}) => {
      if (reset) {
        setLoading(true);
        setErr("");
        setOffset(0);
        offsetRef.current = 0;
        setHasMore(true);
        hasMoreRef.current = true;
      } else {
        if (loadingMoreRef.current || !hasMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const nextOffset = reset ? 0 : offsetRef.current;
        const params = {
          limit: PAGE_SIZE,
          offset: nextOffset,
        };
        if (q) params.q = q;
        if (noFiltro) params.no = noFiltro;

        const { data } = await api.get("/equipamentos/listar", { params });
        const chunk = Array.isArray(data?.data) ? data.data : [];
        setLista((prev) => {
          const next = reset ? chunk : [...prev, ...chunk];
          next.sort((a, b) => {
            const sa = NUM(a?.subdivisao_id);
            const sb = NUM(b?.subdivisao_id);
            if (sa != null && sb != null && sa !== sb) return sa - sb;
            if (sa != null && sb == null) return -1;
            if (sa == null && sb != null) return 1;

            const bySub = TXT(a?.subdivisao_id).localeCompare(TXT(b?.subdivisao_id), "pt-BR", {
              sensitivity: "base",
              numeric: true,
            });
            if (bySub !== 0) return bySub;

            const ka = KMNUM(a?.equipamento_km_inicial);
            const kb = KMNUM(b?.equipamento_km_inicial);
            if (ka != null && kb != null && ka !== kb) return ka - kb;
            if (ka != null && kb == null) return -1;
            if (ka == null && kb != null) return 1;

            return TXT(a?.equipamento_km_inicial).localeCompare(TXT(b?.equipamento_km_inicial), "pt-BR", {
              sensitivity: "base",
              numeric: true,
            });
          });
          return next;
        });
        const newOffset = nextOffset + chunk.length;
        const more = chunk.length === PAGE_SIZE;
        offsetRef.current = newOffset;
        hasMoreRef.current = more;
        setOffset(newOffset);
        setHasMore(more);
      } catch (e) {
        setErr(e?.response?.data?.error || e.message || "Erro ao listar equipamentos");
        if (reset) setLista([]);
        hasMoreRef.current = false;
        setHasMore(false);
      } finally {
        setLoading(false);
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    },
    [q, noFiltro]
  );

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  useEffect(() => {
    carregarPagina({ reset: true });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [q, noFiltro, carregarPagina]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || loadingMoreRef.current || !hasMoreRef.current) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 220) {
        carregarPagina({ reset: false });
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, carregarPagina]);

  function goHome() {
    if (onBack) onBack();
    else if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  async function salvarCriar(payload) {
    await api.post("/equipamentos/cadastrar", payload);
    setAbrirCriar(false);
    await Promise.all([carregarResumo(), carregarPagina({ reset: true })]);
  }

  async function salvarEditar(payload) {
    if (!editarItem?.id) return;
    await api.put(`/equipamentos/editar/${editarItem.id}`, payload);
    setEditarItem(null);
    await Promise.all([carregarResumo(), carregarPagina({ reset: true })]);
  }

  async function confirmarExcluir() {
    if (!excluirItem?.id) return;
    await api.delete(`/equipamentos/excluir/${excluirItem.id}`);
    setExcluirItem(null);
    await Promise.all([carregarResumo(), carregarPagina({ reset: true })]);
  }

  const chips = useMemo(() => resumo.chips || [], [resumo]);

  function renderColGroup() {
    return (
      <colgroup>
        <col style={{ width: "15%" }} />
        <col style={{ width: "7%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "7%" }} />
        <col style={{ width: "7%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "5%" }} />
        <col style={{ width: "10%" }} />
      </colgroup>
    );
  }

  return (
    <div className="eqpm-shell">
      <div className="eqpm-topbar">
        <button className="eqpm-btn-hero left" type="button" onClick={goHome}>
          Voltar para o início
        </button>

        <h1 className="eqpm-title">Catálogo de Equipamentos</h1>

        <button className="eqpm-btn-hero right" type="button" onClick={() => setAbrirCriar(true)}>
          Cadastrar
        </button>
      </div>

      <div className="eqpm-head">
        <div className="eqpm-cards-row eqpm-cards--all">
          <div className="eqpm-search-panel">
            <div className="eqpm-search-panel__label">PESQUISA</div>
            <input
              className="eqpm-search"
              placeholder="Buscar por nome, tipo, KM, gerência, SB..."
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
          </div>

          <div className="eqpm-card">
            <div className="eqpm-card__label">TOTAL</div>
            <div className="eqpm-card__value">{resumo.total}</div>
          </div>

          <div className="eqpm-card">
            <div className="eqpm-card__label">NÓS</div>
            <div className="eqpm-card__value">{resumo.nos}</div>
          </div>

          <div className="eqpm-card">
            <div className="eqpm-card__label">GERÊNCIAS</div>
            <div className="eqpm-card__value">{resumo.gerencias}</div>
          </div>

          <div className="eqpm-card">
            <div className="eqpm-card__label">SBs</div>
            <div className="eqpm-card__value">{resumo.sbs}</div>
          </div>

          <div className="eqpm-chips-panel">
            <div className="eqpm-chips-panel__label">POR NÓ</div>
            <div className="eqpm-chip-list">
              {chips.map(([no, qtd]) => (
                <button
                  key={no}
                  className={`eqpm-chip ${noFiltro && UP(noFiltro) === UP(no) ? "active" : ""}`}
                  type="button"
                  onClick={() => setNoFiltro((prev) => (prev && UP(prev) === UP(no) ? null : no))}
                >
                  {no}
                  <span className="eqpm-chip__num">{qtd}</span>
                </button>
              ))}

              {noFiltro && (
                <button className="eqpm-chip eqpm-chip--clear" type="button" onClick={() => setNoFiltro(null)}>
                  Limpar filtro x
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="eqpm-wrap">
        {loading ? (
          <div className="eqpm-empty">Carregando...</div>
        ) : err ? (
          <div className="eqpm-empty">{err}</div>
        ) : lista.length === 0 ? (
          <div className="eqpm-empty">Nenhum equipamento encontrado.</div>
        ) : (
          <div className="eqpm-table-wrap">
            <table className="eqpm-table eqpm-table--head">
              {renderColGroup()}
              <thead>
                <tr>
                  <th>EQUIPAMENTO</th>
                  <th>TIPO</th>
                  <th>SUBDIVISÃO ID</th>
                  <th>KM INÍCIO</th>
                  <th>KM FINAL</th>
                  <th>GERÊNCIA</th>
                  <th>COORDENADOR</th>
                  <th>SUBDIVISÃO</th>
                  <th>SB</th>
                  <th>OPERAÇÃO</th>
                  <th>ENTRE ESTAÇÕES</th>
                  <th>NÓ</th>
                  <th>CONTROLES</th>
                </tr>
              </thead>
            </table>

            <div ref={scrollRef} className="eqpm-table-body-scroll">
              <table className="eqpm-table eqpm-table--body">
                {renderColGroup()}
                <tbody>
                  {lista.map((row) => (
                    <tr key={row.id}>
                      <td className="eqpm-td eqpm-td-strong">{UP(row.equipamento) || "-"}</td>
                      <td className="eqpm-td">{UP(row.equipamento_tipo) || "-"}</td>
                      <td className="eqpm-td">{UP(row.subdivisao_id) || "-"}</td>
                      <td className="eqpm-td">{TXT(row.equipamento_km_inicial) || "-"}</td>
                      <td className="eqpm-td">{TXT(row.equipamento_km_final) || "-"}</td>
                      <td className="eqpm-td">{UP(row.gerencia_nome) || "-"}</td>
                      <td className="eqpm-td">{UP(row.coordenador_nome) || "-"}</td>
                      <td className="eqpm-td">{UP(row.subdivisao_nome) || "-"}</td>
                      <td className="eqpm-td">{UP(row.sb) || "-"}</td>
                      <td className="eqpm-td">{UP(row.operacao) || "-"}</td>
                      <td className="eqpm-td">{UP(row.entre_estacoes) || "-"}</td>
                      <td className="eqpm-td">{UP(row.no) || "-"}</td>
                      <td className="eqpm-td eqpm-td-controls">
                        <div className="eqpm-actions eqpm-actions--cluster">
                          <button className="eqpm-icon-btn" title="Editar" onClick={() => setEditarItem(row)}>
                            <IconEdit />
                          </button>
                          <span className="eqpm-actions-divider" aria-hidden="true" />
                          <button className="eqpm-icon-btn" title="Excluir" onClick={() => setExcluirItem(row)}>
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {loadingMore ? <div className="eqpm-empty">Carregando mais...</div> : null}
              {!hasMore && lista.length > 0 ? <div className="eqpm-empty">Fim dos registros.</div> : null}
            </div>
          </div>
        )}
      </div>

      {abrirCriar && (
        <CriarEquipamento open onClose={() => setAbrirCriar(false)} onSalvar={salvarCriar} />
      )}

      {editarItem && (
        <EditarEquipamento
          open
          registro={editarItem}
          onClose={() => setEditarItem(null)}
          onSalvar={salvarEditar}
        />
      )}

      {excluirItem && (
        <ExcluirEquipamento
          open
          titulo={excluirItem.equipamento}
          onClose={() => setExcluirItem(null)}
          onConfirmar={confirmarExcluir}
        />
      )}
    </div>
  );
}
